<template>
    <v-container fluid class="pa-8 bg-grey-lighten-5 h-100">
        <div class="d-flex justify-space-between align-center mb-6">
            <div>
                <h2 class="text-h4 font-weight-bold text-grey-darken-3">메타 관리</h2>
                <p class="text-subtitle-1 text-grey-darken-1 mt-1">시스템에 등록된 데이터베이스 메타 정보를 관리합니다.</p>
            </div>
        </div>

        <v-card class="rounded-xl border-none shadow-soft" elevation="0" color="white">
            <div class="pa-6 d-flex justify-space-between align-center">
                <div class="d-flex align-center">
                    <v-avatar color="indigo-lighten-5" class="mr-4" size="48">
                        <v-icon color="indigo-darken-2" size="24">mdi-database-settings</v-icon>
                    </v-avatar>
                    <div>
                        <h3 class="text-h6 font-weight-bold text-grey-darken-3">메타 목록</h3>
                        <span class="text-caption text-grey-darken-1">총 {{ metaList.length }}개의 메타 정보가 있습니다</span>
                    </div>
                </div>
                <div class="d-flex align-center">
                    <v-select v-model="systemFilter" :items="systemOptions" label="시스템 선택" variant="outlined"
                        density="compact" hide-details class="mr-4" style="width: 180px;"
                        @update:model-value="fetchMetaList"></v-select>
                    <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="검색" single-line hide-details
                        variant="outlined" density="compact" class="mr-4 search-field"
                        style="width: 280px; border-radius: 12px;" bg-color="grey-lighten-5"
                        color="indigo"></v-text-field>
                    <v-btn color="indigo-darken-2" prepend-icon="mdi-plus" @click="openDialog" elevation="2" height="40"
                        class="px-6 text-capitalize rounded-lg font-weight-bold">
                        메타 추가
                    </v-btn>
                </div>
            </div>

            <v-divider class="border-opacity-50"></v-divider>

            <v-data-table :headers="headers" :items="metaList" :search="search" :loading="loading"
                class="pa-2 modern-table" hover :items-per-page="10">
                <template v-slot:[metaIdSlot]="{ item }">
                    <div class="text-body-2 text-grey-darken-2">
                        {{ item.raw.metaId }}
                    </div>
                </template>
                <template v-slot:[sysIdSlot]="{ item }">
                    <v-chip size="small" :color="getSystemColor(item.raw.sysId)" variant="flat" label>
                        {{ getSystemName(item.raw.sysId) }}
                    </v-chip>
                </template>
                <template v-slot:[metaNameSlot]="{ item }">
                    <div class="font-weight-bold text-indigo-darken-2 cursor-pointer" @click="editItem(item.raw)">
                        {{ item.raw.metaName }}
                    </div>
                </template>
                <template v-slot:[metaContentSlot]="{ item }">
                    <div class="text-truncate text-grey-darken-2" style="max-width: 400px;">
                        {{ item.raw.metaContent }}
                    </div>
                </template>
                <template v-slot:[regDtSlot]="{ item }">
                    <div class="text-body-2 text-grey-darken-1">
                        {{ formatDate(item.raw.regDt) }}
                    </div>
                </template>
                <template v-slot:[modDtSlot]="{ item }">
                    <div class="text-body-2 text-grey-darken-1">
                        {{ formatDate(item.raw.modDt) }}
                    </div>
                </template>
                <template v-slot:[actionsSlot]="{ item }">
                    <v-icon size="small" class="me-2" color="indigo-lighten-1" @click="editItem(item.raw)">
                        mdi-pencil
                    </v-icon>
                    <v-icon size="small" color="red-lighten-2" @click="deleteItem(item.raw)">
                        mdi-delete
                    </v-icon>
                </template>
            </v-data-table>
        </v-card>

        <!-- Use persistent prop to prevent closing on outside click -->
        <v-dialog v-model="dialog" width="80%" persistent>
            <v-card class="rounded-xl shadow-soft d-flex flex-column"
                style="height: 80vh; min-height: 700px; overflow: hidden;">
                <v-card-title
                    class="pa-5 d-flex justify-space-between align-center border-bottom bg-grey-lighten-5 flex-grow-0">
                    <span class="text-h5 font-weight-bold text-grey-darken-3">{{ formTitle }}</span>
                    <v-btn icon="mdi-close" variant="text" color="grey" @click="closeDialog"
                        density="comfortable"></v-btn>
                </v-card-title>

                <v-divider></v-divider>

                <div class="pa-6 flex-grow-1 d-flex flex-column" style="overflow-y: hidden;">
                    <div class="d-flex mb-4">
                        <v-select v-model="editedItem.sysId" :items="systemOptions.filter(o => o.value !== 'ALL')"
                            label="대상 시스템" variant="outlined" color="indigo" bg-color="grey-lighten-5"
                            hide-details="auto" class="mr-4" style="flex: 1;"></v-select>
                        <v-text-field v-model="editedItem.metaId" label="메타 아이디" variant="outlined" color="indigo"
                            bg-color="grey-lighten-5" hide-details="auto" prefix="🆔" class="mr-4" style="flex: 1;"
                            hint="rag_meta_xxxx 형식으로 입력하세요." persistent-hint></v-text-field>
                        <v-text-field v-model="editedItem.metaName" label="메타데이터명" variant="outlined" color="indigo"
                            bg-color="grey-lighten-5" hide-details="auto" prefix="📝" style="flex: 2;"></v-text-field>
                    </div>
                    <div class="flex-grow-1 d-flex flex-column" style="min-height: 0;">
                        <v-textarea v-model="editedItem.metaContent" label="메타데이터 내용" variant="outlined" color="indigo"
                            bg-color="grey-lighten-5" hide-details prepend-inner-icon="mdi-text-box-outline" no-resize
                            class="flex-grow-1 full-height-textarea" style="height: 100%;"></v-textarea>
                    </div>
                </div>

                <v-divider></v-divider>

                <v-card-actions class="pa-5 bg-grey-lighten-5 flex-grow-0">
                    <v-spacer></v-spacer>
                    <v-btn color="grey-darken-1" variant="text" @click="closeDialog" class="px-6 rounded-lg mr-2"
                        height="40">
                        취소
                    </v-btn>
                    <v-btn color="indigo-darken-2" variant="flat" @click="save" class="px-6 rounded-lg" elevation="2"
                        height="40" :loading="saveLoading">
                        저장
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete Confirmation Dialog -->
        <v-dialog v-model="dialogDelete" max-width="400px">
            <v-card class="rounded-xl text-center pa-4">
                <v-card-text class="text-h6 text-grey-darken-3 py-6">
                    정말 삭제하시겠습니까?
                </v-card-text>
                <v-card-actions class="justify-center pb-4">
                    <v-btn color="grey-darken-1" variant="outlined" class="px-6 rounded-lg mr-2"
                        @click="closeDelete">취소</v-btn>
                    <v-btn color="red-darken-1" variant="flat" class="px-6 rounded-lg" @click="deleteItemConfirm"
                        :loading="deleteLoading">삭제</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const axios = proxy.$axios;

const systemFilter = ref('ALL');
const systemOptions = ref([
    { title: '전체', value: 'ALL' },
]);

const fetchSystemInfo = async () => {
    try {
        const res = await axios.post('/common/sys-info', {});
        if (res.data.status === 'success') {
            const list = res.data.data.list;
            systemOptions.value = [
                { title: '전체', value: 'ALL' },
                ...list.map(s => ({ title: s.sysNm, value: s.sysId }))
            ];
        }
    } catch (err) {
        console.error('Failed to fetch system info:', err);
    }
};

const getSystemName = (sysId) => {
    const option = systemOptions.value.find(o => o.value === sysId);
    return option ? option.title : sysId;
};

const systemColors = ['blue-darken-2', 'teal-darken-1', 'deep-purple-darken-1', 'orange-darken-2', 'cyan-darken-2'];

const getSystemColor = (sysId) => {
    const index = systemOptions.value.findIndex(option => option.value === sysId);
    return index > 0 ? systemColors[(index - 1) % systemColors.length] : 'grey';
};

const getDefaultSystemId = () => systemOptions.value.find(option => option.value !== 'ALL')?.value || null;

const search = ref('');
const loading = ref(false);
const saveLoading = ref(false);
const deleteLoading = ref(false);
const dialog = ref(false);
const dialogDelete = ref(false);

const headers = [
    { title: '메타 아이디', key: 'metaId', align: 'start', width: '12%' },
    { title: '시스템', key: 'sysId', align: 'start', width: '10%' },
    { title: '메타데이터명', key: 'metaName', align: 'start', width: '18%' },
    { title: '메타데이터 내용', key: 'metaContent', align: 'start', width: '30%' },
    { title: '생성일', key: 'regDt', align: 'start', width: '12%' },
    { title: '수정일', key: 'modDt', align: 'start', width: '10%' },
    { title: '관리', key: 'actions', align: 'center', sortable: false, width: '8%' },
];

// Slot names
const metaIdSlot = 'item.metaId';
const sysIdSlot = 'item.sysId';
const metaNameSlot = 'item.metaName';
const metaContentSlot = 'item.metaContent';
const regDtSlot = 'item.regDt';
const modDtSlot = 'item.modDt';
const actionsSlot = 'item.actions';

const metaList = ref([]);
const editedIndex = ref(-1);
const editedItem = reactive({
    metaId: '',
    metaName: '',
    metaContent: '',
    sysId: null,
});
const defaultItem = {
    metaId: '',
    metaName: '',
    metaContent: '',
    sysId: null,
};

const formTitle = computed(() => {
    return editedIndex.value === -1 ? '메타 정보 추가' : '메타 정보 수정';
});

onMounted(() => {
    fetchSystemInfo();
    fetchMetaList();
});

const fetchMetaList = () => {
    loading.value = true;
    const params = {};
    if (systemFilter.value !== 'ALL') {
        params.sysId = systemFilter.value;
    }

    axios.post('/rag/database/meta/list', params, { headers: { 'Content-Type': 'application/json' } })
        .then(res => {
            if (res.data.status === 'success') {
                metaList.value = res.data.data.list || [];
            } else {
                console.error(res.data.message);
            }
        })
        .finally(() => {
            loading.value = false;
        });
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Handle both array format [y, m, d, h, m, s] and string ISO format
    if (Array.isArray(dateString)) {
        const [year, month, day, hour, minute, second] = dateString;
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
    }
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
};

const openDialog = () => {
    editedIndex.value = -1;
    Object.assign(editedItem, defaultItem);
    if (systemFilter.value !== 'ALL') {
        editedItem.sysId = systemFilter.value;
    } else {
        editedItem.sysId = getDefaultSystemId();
    }
    dialog.value = true;
};

const editItem = (item) => {
    editedIndex.value = metaList.value.indexOf(item);
    // Initially set what we have (truncated content)
    Object.assign(editedItem, {
        metaId: item.metaId,
        metaName: item.metaName,
        metaContent: item.metaContent,
        sysId: item.sysId
    });

    // Fetch full content
    loading.value = true;
    axios.post('/rag/database/meta/detail', { metaId: item.metaId }, { headers: { 'Content-Type': 'application/json' } })
        .then(res => {
            if (res.data.status === 'success') {
                editedItem.metaContent = res.data.data.info ? res.data.data.info.metaContent : '';
                dialog.value = true;
            } else {
                alert('상세 정보 로드 실패: ' + res.data.message);
            }
        })
        .catch(err => {
            console.error(err);
            alert('상세 정보를 불러오는 중 오류가 발생했습니다.');
        })
        .finally(() => {
            loading.value = false;
        });
};

const deleteItem = (item) => {
    editedIndex.value = metaList.value.indexOf(item);
    Object.assign(editedItem, item);
    dialogDelete.value = true;
};

const deleteItemConfirm = () => {
    deleteLoading.value = true;
    axios.post('/rag/database/meta/delete', { metaId: editedItem.metaId }, { headers: { 'Content-Type': 'application/json' } })
        .then(res => {
            if (res.data.status === 'success') {
                metaList.value.splice(editedIndex.value, 1);
                closeDelete();
            } else {
                alert('삭제 실패: ' + res.data.message);
            }
        })
        .finally(() => {
            deleteLoading.value = false;
        });
};

const closeDialog = () => {
    dialog.value = false;
    setTimeout(() => {
        Object.assign(editedItem, defaultItem);
        editedIndex.value = -1;
    }, 300);
};

const closeDelete = () => {
    dialogDelete.value = false;
    setTimeout(() => {
        Object.assign(editedItem, defaultItem);
        editedIndex.value = -1;
    }, 300);
};

const save = () => {
    if (!editedItem.metaName) {
        alert('메타데이터명을 입력해주세요.');
        return;
    }

    saveLoading.value = true;
    axios.post('/rag/database/meta/save', editedItem, { headers: { 'Content-Type': 'application/json' } })
        .then(res => {
            if (res.data.status === 'success') {
                fetchMetaList();
                closeDialog();
            } else {
                alert('저장 실패: ' + res.data.message);
            }
        })
        .finally(() => {
            saveLoading.value = false;
        });
};
</script>

<style scoped>
.shadow-soft {
    box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05) !important;
}

.cursor-pointer {
    cursor: pointer;
}

.cursor-pointer:hover {
    text-decoration: underline;
}

.full-height-textarea :deep(.v-input__control),
.full-height-textarea :deep(.v-field),
.full-height-textarea :deep(.v-field__field) {
    height: 100% !important;
}

.full-height-textarea :deep(textarea) {
    height: 100% !important;
}
</style>
